import React, { useState } from "react";
import { AtSymbolIcon, KeyIcon } from "@heroicons/react/24/outline";
import { ArrowRightIcon } from "@heroicons/react/20/solid";
import { LoadingButton} from "@/components/ui/loading-button";
import {getUserToken, login, loginWithGoogle, logout} from "@/firebase";
import { useNavigate } from "react-router-dom";
import "@/components/ui/google/google.css";
import {useAppDispatch} from "@/hooks/reducer-hooks";
import {setBuildings, setSubscriptionPromotions, setUserDetails} from "@/slices/userSlice";
import {getSubscriptionPromotions} from "@/services/userDataService";


export default function LoginForm() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();


    const handleLoginSuccess = async () => {
        // Check if the user has is a landlord
        await getSubscriptionPromotions().then((promotions) => {
            dispatch(setSubscriptionPromotions(promotions))
            const buildings = Array.from(new Set(promotions.flatMap((promotion) => promotion.buildings).filter(Boolean)))
            dispatch(setBuildings(buildings))
            navigate("/panel");
        }).catch(
            (error) => {
                logout();
                setError("You are not authorized to access this page. Please contact support.");
                setLoading(false);
                setGoogleLoading(false);
            }
        )
    };


    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(""); // Clear previous error
        setLoading(true);
        try {
            const user = await login(email, password);
            // save token to local storage
            localStorage.setItem('token', await getUserToken());
            dispatch(setUserDetails(user));
            console.log("Logged in as", user);
            // Handle successful login (e.g., redirect)
            await handleLoginSuccess();
        } catch (err) {
            setError("Login failed. Please check your credentials.");
            console.log(err);
            setLoading(false);
        }
    };

    const handleGoogleLogin = async () => {
        setError(""); // Clear previous error
        try {
            setGoogleLoading(true);
            const user = await loginWithGoogle();
            console.log("Logged in as", user);
            await handleLoginSuccess();
            // Handle successful Google login (e.g., redirect)
        } catch (err) {
            setError("Google login failed. Please try again.");
            console.log(err);
        }
        finally {
            setGoogleLoading(false);
        }
    };

    return (
        <form className="space-y-3" onSubmit={handleSubmit}>
            <div className="flex-1 rounded-lg bg-gray-50 px-6 pb-4 pt-8">
                <h1 className={`mb-3 text-2xl`}>Please log in to continue.</h1>
                <div className="w-full">
                    <div>
                        <label
                            className="mb-3 mt-5 block text-xs font-medium text-gray-900 text-left"
                            htmlFor="email"
                        >
                            Email
                        </label>
                        <div className="relative">
                            <input
                                className="peer block w-full rounded-md border border-gray-200 py-[9px] pl-10 text-sm outline-2 placeholder:text-gray-500"
                                id="email"
                                type="email"
                                name="email"
                                placeholder="Enter your email address"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                            <AtSymbolIcon className="pointer-events-none absolute left-3 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-gray-500 peer-focus:text-gray-900" />
                        </div>
                    </div>
                    <div className="mt-4">
                        <label
                            className="mb-3 mt-5 block text-xs font-medium text-gray-900 text-left"
                            htmlFor="password"
                        >
                            Password
                        </label>
                        <div className="relative">
                            <input
                                className="peer block w-full rounded-md border border-gray-200 py-[9px] pl-10 text-sm outline-2 placeholder:text-gray-500"
                                id="password"
                                type="password"
                                name="password"
                                placeholder="Enter password"
                                required
                                minLength={6}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                            <KeyIcon className="pointer-events-none absolute left-3 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-gray-500 peer-focus:text-gray-900" />
                        </div>
                    </div>
                </div>
                <LoadingButton className="mt-4 w-full" loading={loading}>
                    Log in <ArrowRightIcon className="ml-auto h-5 w-5 text-gray-50" />
                </LoadingButton>
                <LoadingButton
                    type="button"
                    loading={googleLoading}
                    className="mt-4 w-full login-with-google-btn"
                    onClick={handleGoogleLogin}
                >
                    Log in with Google
                </LoadingButton>
                {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
            </div>
        </form>
    );
}
